import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, ShieldAlert, Activity, Clock, Calendar, Globe } from "lucide-react";
import { http } from "../../axios/axios";

const API_BASE = import.meta.env.VITE_API_BASE || "https://api.suraksha24x7.com";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await http.get(`/events/${id}`);
        setEvent(res.data?.data || res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchEvent();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-6 h-6 border border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen flex items-center justify-center font-black uppercase tracking-widest">
      404 / Node Offline
    </div>
  );

  const imageUrl = event.image 
    ? `${API_BASE}/${event.image.replace(/\\/g, "/")}` 
    : "https://images.unsplash.com/photo-1557597774-9d273605dfa9?q=80&w=2000";

  return (
    <div className="bg-white min-h-screen pb-24 font-sans">
      
      {/* --- CINEMATIC HERO (Clean, No Overlay Text) --- */}
      <section className="relative w-full h-[50vh] md:h-[60vh] bg-black overflow-hidden">
        <img 
          src={imageUrl} 
          className="absolute inset-0 w-full h-full object-cover opacity-70 grayscale hover:grayscale-0 transition-all duration-1000" 
          alt={event.title} 
        />
        
        <button 
          onClick={() => navigate(-1)} 
          className="absolute top-10 left-6 md:left-16 z-20 flex items-center gap-2 text-[10px] font-black text-white uppercase tracking-widest hover:text-blue-500 transition-colors"
        >
          <ArrowLeft size={14} /> System Back
        </button>

        <div className="absolute bottom-10 left-6 md:left-16 z-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            <span className="text-[10px] font-black text-white/70 uppercase tracking-[0.4em]">
              Briefing ID: {event._id?.slice(-6)}
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white leading-[0.9] tracking-tighter uppercase max-w-4xl">
            {event.title}
          </h1>
        </div>
      </section>

      {/* --- MAIN INFO SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Technical Data Grid (Moved out of Hero) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-slate-100 mb-20">
          {[
            { label: "Location", val: event.location, icon: <MapPin size={14} /> },
            { label: "Deployment", val: new Date(event.startDate).toLocaleDateString(), icon: <Calendar size={14} /> },
            { label: "Timeline", val: `${event.time} HRS`, icon: <Clock size={14} /> },
            { label: "Registry", val: event.name || "UNRESTRICTED", icon: <Globe size={14} /> }
          ].map((item, i) => (
            <div key={i} className="flex flex-col gap-3 p-8 border-r last:border-r-0 border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2 text-blue-600">
                {item.icon}
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{item.label}</span>
              </div>
              <span className="text-sm font-black text-black uppercase tracking-tight">{item.val}</span>
            </div>
          ))}
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Left: Article */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8 text-blue-600">
              <ShieldAlert size={18} />
              <h2 className="text-[10px] font-black uppercase tracking-widest">Operational Briefing</h2>
            </div>
            
            <article className="prose prose-slate max-w-none prose-p:text-slate-600 prose-p:text-lg prose-strong:text-black">
              <p className="font-bold text-xl text-black mb-10 leading-tight border-l-4 border-blue-600 pl-6">
                {event.description}
              </p>
              {event.content && (
                <div 
                  className="mt-8 text-slate-700 leading-relaxed" 
                  dangerouslySetInnerHTML={{ __html: event.content }} 
                />
              )}
            </article>
          </div>

          {/* Right: Action Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-32 border border-slate-200 p-10 bg-white">
              <div className="flex items-center gap-2 mb-6">
                <Activity size={14} className="text-blue-600" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">RSVP Protocol Active</span>
              </div>
              
              <h3 className="text-2xl font-black uppercase tracking-tighter mb-4">Secure Entry</h3>
              <p className="text-slate-500 text-xs mb-10 leading-relaxed">
                Confirm your credentials to attend this node engagement. Registration is mandatory for PSARA compliance.
              </p>
              
              <button className="w-full bg-black text-white py-5 font-black uppercase text-[10px] tracking-[0.2em] hover:bg-blue-600 transition-all duration-300 shadow-xl shadow-black/5">
                Register Node Access
              </button>

              <div className="mt-10 pt-8 border-t border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Node Status</p>
                <p className="text-[10px] font-black text-green-600 uppercase mt-1">Online / Accepting Registrations</p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default EventDetails;